import React, { createContext, useContext, useMemo } from 'react'
import { CUSTOM_GLOBAL_CUSTOMER_VERSION_KEYS } from '../../constants'

const initial = {
  customer: null,
  language: null,
  botOpen: false,
  versionKey: 'v1'
}

const AppContext = createContext(initial)

/**
 * Provider for the AppContext with globally accessible memoized values that
 * avoid prop drilling.
 *
 * @summary This Context Provider allows easy access to a fixed global state.
 * The application utilizes Redux to handle state in a similar (more verbose way).
 * Here, we take advantage of `React.createContext` and `React.useContext` to pass
 * down a central store of memoized values that children can now easily consume.
 * In a larger refactor, each `HOC` and set of Redux `actions, store, selectors`
 * can be simplified as distributed `createContext` with `useReducer` hooks.
 *
 * @todo `v1` is technically inconsequental and is here for the sake of completeness.
 *
 * @param {{
 *   customer: string,
 *   language: string,
 *   botOpen: boolean,
 *   versionKey: string,
 *   getOption: function,
 *   children: React.ElementType
 * }}
 * @return {React.ReactNode}
 */
export default function AppContextProvider({
  customer,
  language,
  botOpen,
  versionKey,
  children,
  getOption
}) {
  const version = getOption('header.loadFromApi') && getOption('header.version')

  const appValue = useMemo(
    () => ({
      customer,
      language,
      botOpen,
      versionKey: CUSTOM_GLOBAL_CUSTOMER_VERSION_KEYS[version] || 'v1'
    }),
    [customer, language, botOpen, versionKey]
  )
  return (
    <AppContext.Provider value={appValue}>
      <React.Fragment>{children}</React.Fragment>
    </AppContext.Provider>
  )
}
export const useAppContext = () => useContext(AppContext)



// WEBPACK FOOTER //
// ./src/containers/App/Context.js